import { makeStyles } from '@mui/styles';
import { Box } from 'apollo-react';
import { neutral4 } from 'apollo-react/colors';
import Icon from 'apollo-react/components/Icon';
import IconButton from 'apollo-react/components/IconButton';
import Tag from 'apollo-react/components/Tag';
import { Search } from 'apollo-react-icons';
import ArrowDown from 'apollo-react-icons/ArrowDown';
import ArrowRight from 'apollo-react-icons/ArrowRight';
import classNames from 'classnames';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  addExpandedNodes,
  closeExpandedNode,
  fetchProductChildrenByItemId,
} from '../../../actions/ProductHierarchyActions';
import { getIsNodeExpanded } from '../../../selectors/ProductHierarchySelectors';
import { getUITA } from '../../../utils/commonUtils';
import { getColorByNodeName } from '../../../utils/productHierarchyUtils';
import { ARIA_LABELS, NODE_MARGIN, NODE_NAMES } from '../constants';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    marginLeft: 32,
    listStyleType: 'none',
  },

  itemContent: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px',
    backgroundColor: `${getColorByNodeName(NODE_NAMES.NODE_NEUTRAL)} !important`,
    borderRadius: '4px',

    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-5px !important',
      border: '5px solid transparent',
      borderLeftColor: 'grey',
      height: '0px !important',
    },

    '&:hover': {
      cursor: 'pointer',
    },

    '&:hover::before': {
      backgroundColor: 'transparent !important',
    },
  },

  selectedNode: {
    backgroundColor: `${getColorByNodeName(NODE_NAMES.NODE_SELECTED)} !important`,
  },

  iconContainer: {
    minHeight: '32px',
    borderRadius: '4px 0 0 4px',
  },

  groupIconContainer: (props) => ({
    padding: '4px 12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderLeft: 'none',
    background: getColorByNodeName(props.entityName),

    '& > svg': {
      fontSize: 16,
    },
  }),

  singleIconContainer: (props) => ({
    marginRight: 0,
    width: 0,
    borderLeft: `solid 5px ${getColorByNodeName(props.entityName)}`,
    background: 'transparent',

    '& > svg': {
      display: 'none',
    },
  }),

  labelContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    minWidth: 0,
    width: '100%',
    height: 32,
    paddingLeft: 5,
    display: 'flex',
    color: 'rgb(68, 68, 68)',

    '&:hover': {
      color: '#000',
      backgroundColor: `${getColorByNodeName(NODE_NAMES.NODE_HOVERED)} !important`,
    },
  },

  label: {
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  labelInfo: {
    display: 'flex',
    alignItems: 'center',
  },

  tag: {
    background: 'transparent',
    color: 'gray',
  },

  expandedLabel: {
    fontWeight: 600,
  },

  searchIcon: {
    '&:hover': {
      backgroundColor: neutral4,
    },
  },

  rootDirections: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -NODE_MARGIN,
      top: -8,
      borderLeft: '2px solid #ccc',
      borderBottom: '2px solid #ccc',
      width: 17,
      height: 23,
    },

    '&::after': {
      position: 'absolute',
      left: -NODE_MARGIN,
      bottom: 0,
      borderLeft: '2px solid #ccc',
      content: '""',
      width: NODE_MARGIN,
      height: '100%',
    },

    '&:last-child::after': {
      display: 'none',
    },
  },

  hideParentDirections: {
    marginLeft: 0,

    '&::before': {
      display: 'none',
    },

    '& > div:nth-of-type(1)::before': {
      display: 'none',
    },
  },

  hideChildDirections: {
    '&::before': {
      display: 'none',
    },
  },

  // animation on expand / collapse
  childrenContainerWrapper: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transition: 'grid-template-rows 0.4s ease-out',
  },

  childrenContainerWrapperInner: {
    overflow: 'hidden',
  },

  showChildrenContainer: {
    gridTemplateRows: '1fr',
  },
});

const HierarchyTreeItem = ({
  id,
  entityName,
  childrenCount,
  totalChildren,
  code,
  label,
  children,
  isGrandParent,
  isSelected,
  handleSelectNode,
  refsList,
}) => {
  const isExpanded = useSelector((state) => getIsNodeExpanded(state, { id }));

  const classes = useStyles({ entityName });
  const hasChildren = !!(childrenCount || totalChildren);

  const dispatch = useDispatch();

  const handleOnSelect = (e) => {
    handleSelectNode(e, id);
  };

  const handleOnExpand = () => {
    if (!hasChildren) {
      return;
    }

    if (isExpanded) {
      dispatch(closeExpandedNode(id));
    } else {
      dispatch(addExpandedNodes([id]));
    }

    if (childrenCount === 0) {
      dispatch(fetchProductChildrenByItemId({ itemId: id, entityName }));
    }
  };

  return (
    <div
      className={classNames(
        classes.root,
        classes.rootDirections,
        isGrandParent && classes.hideParentDirections
      )}
      ui-ta={getUITA('HierarchyTreeItem', entityName, id)}
      ref={(el) => (refsList.current[id] = el)}
    >
      <div
        className={classNames(
          classes.itemContent,
          !hasChildren && classes.hideChildDirections,
          isSelected && classes.selectedNode
        )}
      >
        <Icon
          ui-ta={getUITA('TreeItemExpandIcon', id, isExpanded ? 'Expanded' : '')}
          aria-label={ARIA_LABELS.GROUP_ICON}
          className={classNames(
            classes.iconContainer,
            hasChildren ? classes.groupIconContainer : classes.singleIconContainer
          )}
          onClick={handleOnExpand}
        >
          {isExpanded ? <ArrowDown /> : <ArrowRight />}
        </Icon>
        <Box
          onClick={handleOnSelect}
          className={classNames(
            classes.labelContainer,
            (isExpanded || isSelected) && classes.expandedLabel
          )}
        >
          <span className={classes.label}>{label ? `${code} (${label})` : code}</span>
          {totalChildren > 1 ? (
            <div className={classes.labelInfo}>
              <Tag className={classes.tag} label={totalChildren} />
              <IconButton
                ui-ta={getUITA('TreeItemSearchIcon', id)}
                size="small"
                className={classes.searchIcon}
                aria-label={label}
              >
                <Search aria-label={ARIA_LABELS.SEARCH_MODAL_ICON} />
              </IconButton>
            </div>
          ) : totalChildren === 1 ? (
            <Tag className={classes.tag} label={totalChildren} />
          ) : null}
        </Box>
      </div>
      <div
        className={classNames(
          classes.childrenContainerWrapper,
          hasChildren && isExpanded && classes.showChildrenContainer
        )}
      >
        <div className={classes.childrenContainerWrapperInner}>{children}</div>
      </div>
    </div>
  );
};

export default React.memo(HierarchyTreeItem);
